import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import AppLayout from '../components/AppLayout';
import api from '../api/axiosInstance';
import './CsCalendar.css';
import './CombinedCalendar.css';

const MONTHS_FULL = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];
const MONTHS_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const YEARS = [2024, 2025, 2026];

function formatNumber(num) {
  const abs = Math.abs(Math.round(num || 0));
  const fmt = new Intl.NumberFormat('en-IN').format(abs);
  return num < 0 ? `-${fmt}` : fmt;
}

function fmtVal(v) {
  if (!v) return '0';
  const abs = Math.abs(v);
  if (abs >= 1000) return (v < 0 ? '-' : '') + (abs / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
  return Math.round(v).toString();
}

const SORT_OPTIONS = [
  { key: 'cs',    label: '↓ CS' },
  { key: 'cas',   label: '↓ CAS' },
  { key: 'alpha', label: 'A–Z' },
];

const signCls = (v) => (v > 0 ? 'positive' : v < 0 ? 'negative' : 'neutral');
const hasVal  = (d) => d && d.csValue !== undefined && d.csValue !== 0;

export default function CombinedCalendar() {
  const [year, setYear]       = useState(new Date().getFullYear());
  const [month, setMonth]     = useState(new Date().getMonth() + 1);
  const [csData, setCsData]   = useState(null);
  const [casData, setCasData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tooltip, setTooltip] = useState(null);
  const tooltipRef = useRef(null);

  const [symbol, setSymbol]                 = useState(null);
  const [csSymbols, setCsSymbols]           = useState([]);
  const [casSymbols, setCasSymbols]         = useState([]);
  const [loadingSymbols, setLoadingSymbols] = useState(false);
  const [search, setSearch]                 = useState('');
  const [sortOrder, setSortOrder]           = useState('cs');
  const stockListRef = useRef(null);
  const symbolRef    = useRef(symbol);
  symbolRef.current  = symbol;

  const loadSymbols = useCallback(async () => {
    setLoadingSymbols(true);
    const q = `year=${year}&month=${month}`;
    const [cs, cas] = await Promise.allSettled([
      api.get(`/market/symbols/fo?${q}`),
      api.get(`/market/symbols/cas?${q}`),
    ]);
    setCsSymbols(cs.status === 'fulfilled' ? (cs.value.data?.symbolsData || []) : []);
    setCasSymbols(cas.status === 'fulfilled' ? (cas.value.data?.symbolsData || []) : []);
    setLoadingSymbols(false);
  }, [year, month]);

  useEffect(() => { loadSymbols(); }, [loadSymbols]);

  const loadData = useCallback(async () => {
    setLoading(true);
    const q = `year=${year}&month=${month}`;
    const urlFor = (col) => (symbol
      ? `/market/calendar/${col}?${q}&symbol=${encodeURIComponent(symbol)}`
      : `/market/calendar/${col}/aggregate?${q}`);
    // A symbol may exist in only one collection, so one request can legitimately fail
    const [cs, cas] = await Promise.allSettled([api.get(urlFor('fo')), api.get(urlFor('cas'))]);
    setCsData(cs.status === 'fulfilled' && !cs.value.data?.error ? cs.value.data : null);
    setCasData(cas.status === 'fulfilled' && !cas.value.data?.error ? cas.value.data : null);
    setLoading(false);
  }, [year, month, symbol]);

  useEffect(() => { loadData(); }, [loadData]);

  const stockList = useMemo(() => {
    const map = {};
    csSymbols.forEach(s => {
      map[s.symbol] = { symbol: s.symbol, cs: s.totalCsValue || 0, cas: 0, inCs: true, inCas: false };
    });
    casSymbols.forEach(s => {
      if (!map[s.symbol]) map[s.symbol] = { symbol: s.symbol, cs: 0, cas: 0, inCs: false, inCas: false };
      map[s.symbol].cas   = s.totalCsValue || 0;
      map[s.symbol].inCas = true;
    });
    const q = search.trim().toUpperCase();
    let list = Object.values(map).filter(s => !q || s.symbol.includes(q));
    if (sortOrder === 'cs')       list.sort((a, b) => b.cs - a.cs);
    else if (sortOrder === 'cas') list.sort((a, b) => b.cas - a.cas);
    else                          list.sort((a, b) => a.symbol.localeCompare(b.symbol));
    return list;
  }, [csSymbols, casSymbols, search, sortOrder]);

  const navigateSymbol = useCallback((dir) => {
    if (!stockList.length) return;
    let idx = stockList.findIndex(s => s.symbol === symbolRef.current);
    if (idx === -1) idx = dir === 1 ? 0 : stockList.length - 1;
    else idx = (idx + dir + stockList.length) % stockList.length;
    const next = stockList[idx].symbol;
    setSymbol(next);
    setTimeout(() => {
      stockListRef.current
        ?.querySelector(`[data-symbol="${next}"]`)
        ?.scrollIntoView({ block: 'nearest' });
    }, 0);
  }, [stockList]);

  useEffect(() => {
    const onKey = (e) => {
      const tag = document.activeElement.tagName;
      if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA') return;
      if (e.key === 'ArrowDown')    { e.preventDefault(); navigateSymbol(+1); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); navigateSymbol(-1); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [navigateSymbol]);

  const navPos = (() => {
    if (!symbol) return '';
    const idx = stockList.findIndex(s => s.symbol === symbol);
    return idx !== -1 ? `${idx + 1}/${stockList.length}` : '';
  })();

  const csMap = useMemo(() => {
    const m = {};
    (csData?.dailyData || []).forEach(d => { m[d.day] = d; });
    return m;
  }, [csData]);

  const casMap = useMemo(() => {
    const m = {};
    (casData?.dailyData || []).forEach(d => { m[d.day] = d; });
    return m;
  }, [casData]);

  const summary = useMemo(() => {
    let bothPos = 0, bothNeg = 0;
    const days = new Set([...Object.keys(csMap), ...Object.keys(casMap)]);
    days.forEach(day => {
      const c = csMap[day]?.csValue || 0;
      const a = casMap[day]?.csValue || 0;
      if (c > 0 && a > 0) bothPos++;
      else if (c < 0 && a < 0) bothNeg++;
    });
    return {
      csTotal:  csData?.totalValue  || 0,
      casTotal: casData?.totalValue || 0,
      bothPos,
      bothNeg,
    };
  }, [csMap, casMap, csData, casData]);

  // ── Tooltip ───────────────────────────────────────────────────────────────
  function getTooltipPosition(mx, my) {
    const margin = 8, offset = 12;
    const tw = tooltipRef.current?.offsetWidth  || Math.min(260, window.innerWidth - margin * 2);
    const th = tooltipRef.current?.offsetHeight || 240;
    let x = mx + offset;
    let y = my + offset;
    if (x + tw + margin > window.innerWidth)  x = mx - tw - offset;
    if (y + th + margin > window.innerHeight) y = my - th - offset;
    return {
      x: Math.min(Math.max(x, margin), Math.max(margin, window.innerWidth  - tw - margin)),
      y: Math.min(Math.max(y, margin), Math.max(margin, window.innerHeight - th - margin)),
    };
  }

  function handleEnter(e, day) {
    const { x, y } = getTooltipPosition(e.clientX, e.clientY);
    setTooltip({ x, y, day, cs: csMap[day] || null, cas: casMap[day] || null });
  }
  function handleMove(e) {
    const { x, y } = getTooltipPosition(e.clientX, e.clientY);
    setTooltip(prev => (prev ? { ...prev, x, y } : prev));
  }
  const handleLeave = () => setTooltip(null);

  // ── Month navigation ──────────────────────────────────────────────────────
  function getAdjacentMonth(offset) {
    const t = new Date(Date.UTC(year, month - 1 + offset, 1));
    return {
      year: t.getUTCFullYear(),
      month: t.getUTCMonth() + 1,
      label: `${MONTHS_SHORT[t.getUTCMonth()]} ${t.getUTCFullYear()}`,
    };
  }
  function navigateMonth(offset) {
    const t = getAdjacentMonth(offset);
    setYear(t.year);
    setMonth(t.month);
  }

  // ── Grid ──────────────────────────────────────────────────────────────────
  const peaks = useMemo(() => {
    const find = (map) => {
      let high = null, low = null;
      Object.values(map).forEach(d => {
        if (!hasVal(d)) return;
        if (d.csValue > 0 && (!high || d.csValue > high.csValue)) high = d;
        if (d.csValue < 0 && (!low  || d.csValue < low.csValue))  low  = d;
      });
      return { high: high?.day ?? null, low: low?.day ?? null };
    };
    return { cs: find(csMap), cas: find(casMap) };
  }, [csMap, casMap]);

  function peakOf(series, day) {
    if (peaks[series].high === day) return 'high';
    if (peaks[series].low  === day) return 'low';
    return null;
  }

  function buildMonthGrid() {
    const firstDay    = new Date(year, month - 1, 1).getDay();
    const daysInMonth = new Date(year, month, 0).getDate();
    const startOffset = firstDay === 0 ? 6 : firstDay - 1;

    const headers = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map(d => (
      <div key={d} className="csc-cal-header">{d}</div>
    ));
    const empties = Array.from({ length: startOffset }, (_, i) => (
      <div key={`e${i}`} className="csc-cal-cell empty" />
    ));

    const cells = Array.from({ length: daysInMonth }, (_, i) => {
      const day    = i + 1;
      const cs     = csMap[day];
      const cas    = casMap[day];
      const hasCs  = hasVal(cs);
      const hasCas = hasVal(cas);
      const any    = hasCs || hasCas;
      const align  = hasCs && hasCas
        ? (cs.csValue > 0 && cas.csValue > 0 ? 'aligned-pos'
          : cs.csValue < 0 && cas.csValue < 0 ? 'aligned-neg' : '')
        : '';
      const csPeak  = peakOf('cs', day);
      const casPeak = peakOf('cas', day);

      return (
        <div
          key={day}
          className={[
            'csc-cal-cell', 'cmb-cell',
            any ? 'cmb-has-data' : '',
            align,
            csPeak  ? `cmb-cs-peak cs-${csPeak}`   : '',
            casPeak ? `cmb-cas-peak cas-${casPeak}` : '',
          ].filter(Boolean).join(' ')}
          onMouseEnter={any ? (e) => handleEnter(e, day) : undefined}
          onMouseMove={any ? handleMove : undefined}
          onMouseLeave={any ? handleLeave : undefined}
        >
          <span className="csc-cell-day">{day}</span>
          {any && (
            <div className="cmb-cell-rows">
              <div className={`cmb-cell-row${csPeak ? ` cmb-peak-row cs ${csPeak}` : ''}`}>
                <span className="cmb-tag cs">CS{csPeak && <span className="cmb-peak-icon">{csPeak === 'high' ? '▲' : '▼'}</span>}</span>
                <span className={`cmb-val ${hasCs ? signCls(cs.csValue) : 'neutral'}`}>
                  {hasCs ? formatNumber(cs.csValue) : '—'}
                </span>
              </div>
              <div className={`cmb-cell-row${casPeak ? ` cmb-peak-row cas ${casPeak}` : ''}`}>
                <span className="cmb-tag cas">CAS{casPeak && <span className="cmb-peak-icon">{casPeak === 'high' ? '▲' : '▼'}</span>}</span>
                <span className={`cmb-val ${hasCas ? signCls(cas.csValue) : 'neutral'}`}>
                  {hasCas ? formatNumber(cas.csValue) : '—'}
                </span>
              </div>
            </div>
          )}
        </div>
      );
    });

    const total    = startOffset + daysInMonth;
    const trailing = total % 7 === 0 ? 0 : 7 - (total % 7);
    const tails = Array.from({ length: trailing }, (_, i) => (
      <div key={`t${i}`} className="csc-cal-cell empty" />
    ));

    return [...headers, ...empties, ...cells, ...tails];
  }

  const tooltipDateRaw = tooltip ? (tooltip.cs?.date || tooltip.cas?.date) : null;
  const tooltipDate = tooltip
    ? (tooltipDateRaw
        ? new Date(tooltipDateRaw).toLocaleDateString('en-IN', {
            weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
          })
        : `${tooltip.day} ${MONTHS_FULL[month - 1]} ${year}`)
    : '';

  const renderTtSection = (title, cls, d) => (
    <div className="cmb-tt-section">
      <div className={`cmb-tt-title ${cls}`}>{title}</div>
      {peakOf(cls, tooltip.day) && (
        <div className={`cmb-tt-peak ${cls} ${peakOf(cls, tooltip.day)}`}>
          {peakOf(cls, tooltip.day) === 'high' ? '▲ Highest buying day' : '▼ Highest selling day'}
        </div>
      )}
      {hasVal(d) ? (
        <>
          <div className="csc-tt-row">
            <span className="csc-tt-label">Value:</span>
            <span className={`csc-tt-val ${signCls(d.csValue)}`}>{formatNumber(d.csValue)}</span>
          </div>
          <div className="csc-tt-row">
            <span className="csc-tt-label">Qty:</span>
            <span className={`csc-tt-val ${signCls(d.csQty || 0)}`}>{formatNumber(d.csQty || 0)}</span>
          </div>
          {symbol ? (
            <div className="csc-tt-row">
              <span className="csc-tt-label">Price (IEP):</span>
              <span className="csc-tt-val">{d.avgIEP > 0 ? `₹${d.avgIEP.toFixed(2)}` : '-'}</span>
            </div>
          ) : (
            <div className="csc-tt-row">
              <span className="csc-tt-label">Stocks:</span>
              <span className="csc-tt-val">{d.symbolCount || 0}</span>
            </div>
          )}
        </>
      ) : (
        <div className="cmb-tt-empty">No data</div>
      )}
    </div>
  );

  return (
    <AppLayout pageBadge="Combined Calendar">
      <div className="csc-root">
        <aside className="csc-stock-panel csct-panel cmb-panel">
          <div className="csc-collection-tabs">
            <button
              type="button"
              className={`csc-col-tab${!symbol ? ' active' : ''}`}
              onClick={() => { setSymbol(null); setSearch(''); }}
              title="Show aggregate of all stocks"
            >
              All Stocks
            </button>
          </div>

          <div className="csc-search">
            <input
              type="text"
              placeholder="Search symbol..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>

          <div className="cmb-sort-row">
            {SORT_OPTIONS.map(opt => (
              <button
                key={opt.key}
                type="button"
                className={`cmb-sort-btn${sortOrder === opt.key ? ' active' : ''}`}
                onClick={() => setSortOrder(opt.key)}
              >
                {opt.label}
              </button>
            ))}
          </div>

          <div className="cmb-list-head">
            <span>Symbol</span>
            <span className="cmb-tag cs">CS</span>
            <span className="cmb-tag cas">CAS</span>
          </div>

          <div className="csc-stock-list" ref={stockListRef}>
            {loadingSymbols ? (
              <div className="csc-loading"><div className="csc-spinner" />Loading...</div>
            ) : stockList.length === 0 ? (
              <div className="csc-no-data">No symbols found</div>
            ) : (
              stockList.map(s => (
                <div
                  key={s.symbol}
                  data-symbol={s.symbol}
                  className={`csc-stock-item cmb-stock-item${symbol === s.symbol ? ' active' : ''}`}
                  onClick={() => setSymbol(s.symbol)}
                >
                  <span className="csct-item-sym">{s.symbol}</span>
                  <span className={`cmb-list-val ${s.inCs ? signCls(s.cs) : 'neutral'}`}>
                    {s.inCs ? fmtVal(s.cs) : '—'}
                  </span>
                  <span className={`cmb-list-val ${s.inCas ? signCls(s.cas) : 'neutral'}`}>
                    {s.inCas ? fmtVal(s.cas) : '—'}
                  </span>
                  <a
                    href={`https://in.tradingview.com/chart/?symbol=NSE%3A${encodeURIComponent(s.symbol)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={`View ${s.symbol} on TradingView`}
                    className="csct-tv-link"
                    onClick={e => e.stopPropagation()}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                    </svg>
                  </a>
                </div>
              ))
            )}
          </div>

          <div className="csc-nav-bar">
            <button className="csc-nav-key" onClick={() => navigateSymbol(-1)} title="Previous (↑)">▲</button>
            <button className="csc-nav-key" onClick={() => navigateSymbol(+1)} title="Next (↓)">▼</button>
            <span className="csc-nav-label">navigate</span>
            <span className="csc-nav-pos">{navPos}</span>
          </div>
        </aside>

        <main className="csc-main">
          <div className="csc-filter-row">
            <div className="csc-selectors">
              <div className="csc-selector-group">
                <span className="csc-selector-label">Year:</span>
                <select
                  className="csc-selector-select"
                  aria-label="Year"
                  value={year}
                  onChange={e => setYear(parseInt(e.target.value, 10))}
                >
                  {[...new Set([...YEARS, year])].sort((a, b) => a - b).map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
              <div className="csc-selector-group">
                <span className="csc-selector-label">Month:</span>
                <select
                  className="csc-selector-select"
                  aria-label="Month"
                  value={month}
                  onChange={e => setMonth(parseInt(e.target.value, 10))}
                >
                  {MONTHS_SHORT.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                </select>
              </div>
            </div>
            <div className="cmb-legend">
              <span className="csc-cal-context">{symbol || 'All Stocks'}</span>
              <span className="cmb-tag cs">CS</span> F&amp;O
              <span className="cmb-tag cas">CAS</span> CAS
              <span className="cmb-legend-peak cs">CS Peak</span>
              <span className="cmb-legend-peak cas">CAS Peak</span>
              <span className="cmb-legend-dir high">▲ Buy</span>
              <span className="cmb-legend-dir low">▼ Sell</span>
            </div>
          </div>

          <div className="csc-summary cmb-summary">
            <div className="csc-card">
              <div className="csc-card-label">CS (F&amp;O)</div>
              <div className={`csc-card-value ${summary.csTotal >= 0 ? 'positive' : 'negative'}`}>
                {formatNumber(summary.csTotal)}
              </div>
            </div>
            <div className="csc-card">
              <div className="csc-card-label">CAS</div>
              <div className={`csc-card-value ${summary.casTotal >= 0 ? 'positive' : 'negative'}`}>
                {formatNumber(summary.casTotal)}
              </div>
            </div>
            <div className="csc-card">
              <div className="csc-card-label">Both ▲ Days</div>
              <div className="csc-card-value positive">{summary.bothPos}</div>
            </div>
            <div className="csc-card">
              <div className="csc-card-label">Both ▼ Days</div>
              <div className="csc-card-value negative">{summary.bothNeg}</div>
            </div>
          </div>

          <div className="csc-calendar-section">
            <div className="csc-month-nav" aria-label="Calendar month navigation">
              <button type="button" onClick={() => navigateMonth(-1)}>‹ {getAdjacentMonth(-1).label}</button>
              <span className="csc-cal-month">{MONTHS_FULL[month - 1]} {year}</span>
              <button type="button" onClick={() => navigateMonth(1)}>{getAdjacentMonth(1).label} ›</button>
            </div>
            <div className="csc-cal-grid">
              {loading ? (
                <div className="csc-loading"><div className="csc-spinner" />Loading calendar...</div>
              ) : !csData && !casData ? (
                <div className="csc-no-data">No data available</div>
              ) : (
                buildMonthGrid()
              )}
            </div>
          </div>
        </main>

        {tooltip && (
          <div
            ref={tooltipRef}
            className="csc-tooltip cmb-tooltip"
            style={{ left: tooltip.x, top: tooltip.y }}
          >
            <div className="csc-tt-header">{symbol || 'ALL STOCKS'} · {tooltipDate}</div>
            {renderTtSection('CS · F&O Stocks', 'cs', tooltip.cs)}
            {renderTtSection('CAS · CAS Stocks', 'cas', tooltip.cas)}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
